import {DateTime} from 'luxon';
import moment from 'moment';


/**
 * Converts local date string to local Luxon DateTime object.
 * @param dateString
 * @returns {DateTime}
 */
const toLuxonDateTime = (dateString) => {
    return DateTime.fromISO(dateString);
};

/**
 * Convert local date to JS date object.
 *
 * @param localDateString
 *
 * @returns {Date|null}
 */
const toJSDate = (localDateString) => {
    localDateString = localDateString?.trim();

    if (!localDateString) {
        return null;
    }

    let luxonDate = null;

    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2} [AP]M$/i.test(localDateString)) {
        // Check if the string matches the 'YYYY-MM-DD hh:mm a' format
        luxonDate = DateTime.fromFormat(localDateString, 'yyyy-MM-dd hh:mm a', {zone: 'local'});
    } else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(localDateString)) {
        // Check if the string matches the 'YYYY-MM-DD HH:mm:ss' format
        luxonDate = DateTime.fromFormat(localDateString, 'yyyy-MM-dd HH:mm:ss', {zone: 'local'});
    }

    if (luxonDate && luxonDate.isValid) {
        localDateString = luxonDate.toISO();
    }

    return new Date(localDateString);
};

/**
 * Convert iso date string (utc) to local date string using the specified format.
 *
 * @param isoDateString
 * @param format
 * @returns {string}
 */
const toLocalDate = (isoDateString, format = 'yyyy-LL-dd') => {
    if (!isoDateString) {
        return '';
    }

    return DateTime.fromISO(isoDateString).toFormat(format);
};

/**
 * Convert iso date string (utc) to local date time string using the specified format.
 *
 * @param isoDateString
 * @param format
 * @returns {string}
 */
const toLocalDateTime = (isoDateString, format = 'yyyy-LL-dd, hh:mm:ss a') => {
    if (!isoDateString) {
        return '';
    }

    return DateTime.fromISO(isoDateString).toFormat(format);
};

/**
 /**
 * Converts the dateTime to UTC ISO date string.
 *
 * @param dateTime - can be a luxon DateTime object, moment object, JS date object, or date string
 * @returns {string} - the UTC ISO string
 */
const toUTCISOString = (dateTime) => {

    if (dateTime instanceof DateTime) {
        return dateTime.toUTC().toISO();
    } else if (moment.isMoment(dateTime)) {
        return dateTime.utc().toISOString();
    } else if (dateTime instanceof Date) {
        return dateTime.toISOString();
    } else {
        // Handle the dateTime as string
        dateTime = dateTime?.trim();

        if (!dateTime) {
            return '';
        }

        // If the local date string is already a UTC ISO string, return it directly
        if (dateTime.includes('T') && dateTime.includes('Z')) {
            return dateTime;
        }

        // If the local date string has no time, set to midnight time
        if (!dateTime.includes(':')) {
            dateTime = dateTime.concat(' 00:00:00');
        }

        const date = toJSDate(dateTime);

        return date.toISOString();
    }
};


const formattedDate = (date, format = 'MM-dd-yyyy') => {
    if (!date) {
        return '';
    }

    return DateTime.fromISO(date).toFormat(format);
};

const toStartOfDay = (dateTime) => {
    if (!dateTime) {
        return '';
    }

    return DateTime.fromISO(dateTime).startOf('day');
};


const toEndOfDay = (dateTime) => {
    if (!dateTime) {
        return '';
    }

    return DateTime.fromISO(dateTime).endOf('day');
};


const formatUtcToLocal = (utcStr, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  return DateTime.fromISO(utcStr, { zone: 'utc' }).setZone(timezone).toFormat('yyyy-MM-dd HH:mm')
}

const DateTimeUtil = {
    toLuxonDateTime,
    toJSDate,
    toLocalDate,
    toLocalDateTime,
    toUTCISOString,
    formattedDate,
    toStartOfDay,
    toEndOfDay,
    formatUtcToLocal
};

export default DateTimeUtil;
